import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, FileUp, PackageCheck, Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  approveReleasePublicationRequest,
  createApprovedReleasePublication,
  listReleasePublicationRequests,
  rejectReleasePublicationRequest,
  resolveReleasePublicationRequest
} from "@/api/admin-api";
import type { ReleasePublicationManifest, ReleasePublicationRequestRecord } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/date";

export function ReleasePublicationPage() {
  const { t, i18n } = useTranslation(["releasePublication", "common"]);
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [selected, setSelected] = useState<ReleasePublicationRequestRecord | null>(null);
  const [reason, setReason] = useState("");
  const [manifest, setManifest] = useState<ReleasePublicationManifest | null>(null);
  const [manifestFilename, setManifestFilename] = useState("");
  const [manifestError, setManifestError] = useState(false);

  const pendingParams = new URLSearchParams({ status: "PENDING", limit: "20" });
  const pending = useQuery({
    queryKey: ["release-publication-requests", "PENDING"],
    queryFn: () => listReleasePublicationRequests(pendingParams),
    refetchInterval: 5000
  });
  const resolve = useMutation({
    mutationFn: resolveReleasePublicationRequest,
    onSuccess: (request) => {
      setSelected(request);
      setCode(request.requestCode);
      setReason("");
    }
  });
  const create = useMutation({
    mutationFn: createApprovedReleasePublication,
    onSuccess: (request) => {
      setSelected(request);
      setCode(request.requestCode);
      setManifest(null);
      setManifestFilename("");
      setManifestError(false);
      void queryClient.invalidateQueries({ queryKey: ["release-publication-requests"] });
    }
  });
  const approve = useMutation({
    mutationFn: (request: ReleasePublicationRequestRecord) => approveReleasePublicationRequest(request.requestCode, request.revision),
    onSuccess: refresh
  });
  const reject = useMutation({
    mutationFn: (request: ReleasePublicationRequestRecord) => rejectReleasePublicationRequest(request.requestCode, request.revision, reason),
    onSuccess: refresh
  });

  function refresh(request: ReleasePublicationRequestRecord) {
    setSelected(request);
    setReason("");
    void queryClient.invalidateQueries({ queryKey: ["release-publication-requests"] });
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (normalized) {
      resolve.mutate(normalized);
    }
  }

  async function selectManifest(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setManifest(null);
    setManifestFilename(file?.name ?? "");
    setManifestError(false);
    if (!file) return;
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("invalid manifest");
      setManifest(parsed as ReleasePublicationManifest);
    } catch {
      setManifestError(true);
    }
  }

  const mutating = approve.isPending || reject.isPending;

  return (
    <div className="grid gap-5">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold"><PackageCheck className="h-5 w-5" />{t("title")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
      </div>

      <section className="grid gap-3 rounded-md border bg-panel p-4">
        <div>
          <h3 className="font-semibold">{t("createTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("createDescription")}</p>
        </div>
        <Label htmlFor="publication-manifest">{t("manifestLabel")}</Label>
        <Input id="publication-manifest" type="file" accept="application/json,.json" onChange={(event) => void selectManifest(event)} />
        {manifestFilename && !manifestError ? <p className="text-sm text-muted-foreground">{manifestFilename}</p> : null}
        {manifestError ? <p role="alert" className="text-sm text-destructive">{t("manifestInvalid")}</p> : null}
        <div>
          <Button disabled={!manifest || create.isPending} onClick={() => manifest && create.mutate(manifest)}>
            <FileUp className="h-4 w-4" />{t("create")}
          </Button>
        </div>
        {create.isError ? <p role="alert" className="text-sm text-destructive">{t("createFailed")}</p> : null}
      </section>

      <section className="rounded-md border bg-panel p-4">
        <form className="grid gap-3" onSubmit={submit}>
          <Label htmlFor="publication-request-code">{t("codeLabel")}</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input id="publication-request-code" autoComplete="off" value={code} onChange={(event) => setCode(event.target.value)} placeholder="LPR1-0123456789AB" />
            <Button type="submit" disabled={!code.trim() || resolve.isPending}><Search className="h-4 w-4" />{t("resolve")}</Button>
          </div>
          {resolve.isError ? <p role="alert" className="text-sm text-destructive">{t("resolveFailed")}</p> : null}
        </form>
      </section>

      {selected ? (
        <section className="grid gap-4 rounded-md border bg-panel p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-sm font-semibold">{selected.requestCode}</div>
                <Button type="button" size="icon" variant="ghost" aria-label={t("copyCode")} onClick={() => void navigator.clipboard.writeText(selected.requestCode)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-lg font-semibold">{selected.version}</div>
            </div>
            <Badge variant={statusVariant(selected.status)}>{selected.status}</Badge>
          </div>
          <dl className="grid gap-x-5 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t("fields.application")} value={selected.appId} />
            <Field label={t("fields.environment")} value={selected.deploymentEnvironment} />
            <Field label={t("fields.channel")} value={selected.channel} />
            <Field label={t("fields.source")} value={selected.sourceCommit} mono />
            <Field label={t("fields.filename")} value={selected.artifactFilename} />
            <Field label={t("fields.signing")} value={selected.artifactSigningState} />
            <Field label={t("fields.size")} value={`${selected.artifactSizeBytes.toLocaleString()} B`} />
            <Field label={t("fields.artifactSha256")} value={selected.artifactSha256} mono />
            <Field label={t("fields.manifestSha256")} value={selected.manifestSha256} mono />
            <Field label={t("fields.admissionKey")} value={selected.admissionKeyId} />
            <Field label={t("fields.expires")} value={formatDateTime(selected.expiresAt, i18n.language)} />
          </dl>
          {selected.notes ? <div className="rounded bg-muted p-3 text-sm whitespace-pre-wrap">{selected.notes}</div> : null}
          {selected.status === "PENDING" ? (
            <div className="grid gap-3 border-t pt-4">
              <Label htmlFor="publication-reject-reason">{t("rejectReason")}</Label>
              <Textarea id="publication-reject-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder={t("rejectReasonPlaceholder")} />
              <div className="flex flex-wrap gap-2">
                <Button disabled={mutating} onClick={() => approve.mutate(selected)}><Check className="h-4 w-4" />{t("approve")}</Button>
                <Button variant="destructive" disabled={mutating || !reason.trim()} onClick={() => reject.mutate(selected)}><X className="h-4 w-4" />{t("reject")}</Button>
              </div>
              {approve.isError || reject.isError ? <p role="alert" className="text-sm text-destructive">{t("mutationFailed")}</p> : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="grid gap-3 rounded-md border bg-panel p-4">
        <h3 className="font-semibold">{t("pendingTitle")}</h3>
        {pending.isError ? <p className="text-sm text-destructive">{t("pendingFailed")}</p> : null}
        {pending.data?.items.length === 0 ? <p className="text-sm text-muted-foreground">{t("pendingEmpty")}</p> : null}
        <div className="grid gap-2">
          {pending.data?.items.map((request) => (
            <button key={request.id} type="button" className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-left hover:bg-accent" onClick={() => resolve.mutate(request.requestCode)}>
              <span><span className="font-mono text-xs">{request.requestCode}</span><span className="ml-3 font-medium">{request.version}</span></span>
              <span className="text-xs text-muted-foreground">{request.artifactFilename}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <div className="min-w-0"><dt className="text-xs text-muted-foreground">{label}</dt><dd className={`break-all ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</dd></div>;
}

function statusVariant(status: ReleasePublicationRequestRecord["status"]) {
  if (status === "PUBLISHED" || status === "APPROVED") return "success" as const;
  if (status === "REJECTED") return "destructive" as const;
  if (status === "PENDING") return "warning" as const;
  return "default" as const;
}
